import React from 'react';

const ROLE_LABELS = {
  user: 'Contato',
  assistant: 'Clone',
  system: 'Sistema',
};

const META_DELIVERY_LABELS = {
  sent: 'Enviada',
  delivered: 'Entregue',
  read: 'Lida',
  failed: 'Falha na entrega',
};

const WHATSAPP_FORMAT_PATTERN =
  /```([\s\S]*?)```|\*([^*\n]+)\*|_([^_\n]+)_|~([^~\n]+)~/g;

export function parseWhatsAppFormatting(content = '') {
  const tokens = [];
  let cursor = 0;

  for (const match of content.matchAll(WHATSAPP_FORMAT_PATTERN)) {
    if (match.index > cursor) {
      tokens.push({ type: 'text', text: content.slice(cursor, match.index) });
    }
    if (match[1] !== undefined) {
      tokens.push({ type: 'code', text: match[1] });
    } else if (match[2] !== undefined) {
      tokens.push({ type: 'bold', text: match[2] });
    } else if (match[3] !== undefined) {
      tokens.push({ type: 'italic', text: match[3] });
    } else {
      tokens.push({ type: 'strike', text: match[4] });
    }
    cursor = match.index + match[0].length;
  }

  if (cursor < content.length) {
    tokens.push({ type: 'text', text: content.slice(cursor) });
  }
  return tokens;
}

function FormattedMessage({ content }) {
  return parseWhatsAppFormatting(content).map((token, index) => {
    const key = `${token.type}-${index}`;
    if (token.type === 'bold') return <strong key={key}>{token.text}</strong>;
    if (token.type === 'italic') return <em key={key}>{token.text}</em>;
    if (token.type === 'strike') return <s key={key}>{token.text}</s>;
    if (token.type === 'code') {
      return (
        <code key={key} className="rounded bg-slate-200/70 px-1 font-mono text-[0.9em]">
          {token.text}
        </code>
      );
    }
    return <React.Fragment key={key}>{token.text}</React.Fragment>;
  });
}

function formatDateTime(iso) {
  if (!iso) return null;
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(iso));
}

export function isOperatorMessage(message) {
  return (
    message?.role === 'assistant' &&
    (message?.metadata?.source === 'manual' ||
      message?.metadata?.source === 'botmaker_operator')
  );
}

export function getMetaDeliveryInfo(message) {
  const delivery = message?.role === 'assistant' ? message?.metadata?.meta_delivery : null;
  if (!delivery?.status) return null;

  const error = Array.isArray(delivery.errors) ? delivery.errors[0] : null;
  const errorParts = [error?.code, error?.title].filter(Boolean);
  return {
    status: delivery.status,
    label: META_DELIVERY_LABELS[delivery.status] || delivery.status,
    failed: delivery.status === 'failed',
    error: errorParts.join(' — '),
    details: error?.error_data?.details || '',
  };
}

export default function MessageBubble({ message }) {
  const { role, content, created_at: createdAt } = message;

  if (role === 'system') {
    return (
      <div className="flex justify-center py-1">
        <div className="max-w-[90%] text-center">
          <p className="text-xs text-content-subtle italic whitespace-pre-wrap">{content}</p>
          {createdAt && (
            <p className="text-[10px] text-content-subtle mt-0.5">{formatDateTime(createdAt)}</p>
          )}
        </div>
      </div>
    );
  }

  const isUser = role === 'user';
  const fromOperator = isOperatorMessage(message);
  const metaDelivery = getMetaDeliveryInfo(message);

  const bubbleStyles = isUser
    ? 'bg-slate-100 text-content-primary'
    : fromOperator
      ? 'bg-blue-50 border border-blue-200 text-content-primary'
      : 'bg-forge-50 border border-forge-200 text-content-primary';

  return (
    <div className={`flex ${isUser ? 'justify-start' : 'justify-end'}`}>
      <div className={`max-w-[85%] rounded-xl px-3 py-2 ${bubbleStyles}`}>
        <p className="text-[10px] font-medium text-content-muted mb-0.5">
          {fromOperator
            ? message?.metadata?.operator_name
              ? `Operador (${message.metadata.operator_name})`
              : 'Operador'
            : ROLE_LABELS[role] || role}
        </p>
        <p className="text-sm whitespace-pre-wrap">
          <FormattedMessage content={content} />
        </p>
        {metaDelivery && (
          <p
            className={`mt-1 text-[10px] ${
              metaDelivery.failed ? 'font-medium text-red-700' : 'text-content-subtle'
            }`}
            title={metaDelivery.details || undefined}
          >
            {metaDelivery.label}
            {metaDelivery.error ? `: ${metaDelivery.error}` : ''}
          </p>
        )}
        {createdAt && (
          <p className="text-[10px] text-content-subtle mt-1">{formatDateTime(createdAt)}</p>
        )}
      </div>
    </div>
  );
}
